import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { calendarDay, clockTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { CopyField } from '../../shared/ui/copy-field';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Note } from '../../shared/ui/note';
import { SectionPanel } from '../../shared/ui/section-panel';
import { sourceClientKeys, updateCustomer, type TCustomer } from '../../entities/customer';

interface IClientDetailsProps {
  customer: TCustomer;
  now: number;
  timeZone?: string;
}

// The page's one Save: the name, and the reference while it is still unset.
export const ClientDetails = ({ customer, now, timeZone }: IClientDetailsProps) => {
  const client = useQueryClient();
  const [name, setName] = useState(customer.name);
  const [reference, setReference] = useState(customer.externalId ?? '');

  // The reference is a join key: it can be given once, never changed.
  const referenceFixed = customer.externalId !== null;
  const dirty = name.trim() !== customer.name || (!referenceFixed && reference.trim().length > 0);

  const save = useMutation({
    mutationFn: () =>
      updateCustomer(customer.id, {
        name: name.trim(),
        ...(!referenceFixed && reference.trim() ? { externalId: reference.trim() } : {}),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: sourceClientKeys.everywhere }),
  });

  return (
    <SectionPanel title="Details">
      <div className="flex flex-col gap-4 p-card">
        <div>
          <FieldLabel htmlFor="client-name">Name</FieldLabel>
          <Input id="client-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>

        <div>
          <FieldLabel htmlFor="client-reference">Your reference</FieldLabel>
          <Input
            id="client-reference"
            mono
            disabled={referenceFixed}
            placeholder="optional"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
          <Hint>
            Your own id for them. It is on each of their runs as{' '}
            <code className="font-mono">customer.externalId</code>, so a flow can tell clients
            apart. Once set, it does not change.
          </Hint>
        </div>

        <div>
          <CopyField label="Client id" value={customer.id} />
          <p className="mt-2 font-mono text-micro text-faint">
            Created {calendarDay(customer.createdAt, now, timeZone)}{' '}
            {clockTime(customer.createdAt, timeZone)}
          </p>
        </div>

        {save.isError && <Note tone="danger">Not saved: {save.error.message}</Note>}

        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!dirty || !name.trim()}
            waiting={save.isPending}
            waitingLabel="Saving"
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      </div>
    </SectionPanel>
  );
};
